import { m } from '@pricedrop/i18n';
import { Link, useNavigate } from '@tanstack/react-router';
import AirVent from 'lucide-react/dist/esm/icons/air-vent';
import Check from 'lucide-react/dist/esm/icons/check';
import Coffee from 'lucide-react/dist/esm/icons/coffee';
import Gamepad2 from 'lucide-react/dist/esm/icons/gamepad-2';
import Headphones from 'lucide-react/dist/esm/icons/headphones';
import Home from 'lucide-react/dist/esm/icons/home';
import Joystick from 'lucide-react/dist/esm/icons/joystick';
import Keyboard from 'lucide-react/dist/esm/icons/keyboard';
import Laptop from 'lucide-react/dist/esm/icons/laptop';
import Monitor from 'lucide-react/dist/esm/icons/monitor';
import Printer from 'lucide-react/dist/esm/icons/printer';
import Refrigerator from 'lucide-react/dist/esm/icons/refrigerator';
import Search from 'lucide-react/dist/esm/icons/search';
import Smartphone from 'lucide-react/dist/esm/icons/smartphone';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import Speaker from 'lucide-react/dist/esm/icons/speaker';
import Tablet from 'lucide-react/dist/esm/icons/tablet';
import ToyBrick from 'lucide-react/dist/esm/icons/toy-brick';
import Tv from 'lucide-react/dist/esm/icons/tv';
import WashingMachine from 'lucide-react/dist/esm/icons/washing-machine';
import Watch from 'lucide-react/dist/esm/icons/watch';
import Zap from 'lucide-react/dist/esm/icons/zap';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { FEATURED_CATEGORIES } from '@/lib/featured-categories';
import { itemsHref } from '@/routes/items/-search-params';

const ICON_MAP: Record<string, React.ReactNode> = {
  Smartphone: <Smartphone className="h-3 w-3" />,
  Laptop: <Laptop className="h-3 w-3" />,
  Tv: <Tv className="h-3 w-3" />,
  Headphones: <Headphones className="h-3 w-3" />,
  Gamepad2: <Gamepad2 className="h-3 w-3" />,
  Monitor: <Monitor className="h-3 w-3" />,
  Keyboard: <Keyboard className="h-3 w-3" />,
  Zap: <Zap className="h-3 w-3" />,
  Tablet: <Tablet className="h-3 w-3" />,
  Watch: <Watch className="h-3 w-3" />,
  Speaker: <Speaker className="h-3 w-3" />,
  ToyBrick: <ToyBrick className="h-3 w-3" />,
  Home: <Home className="h-3 w-3" />,
  Sparkles: <Sparkles className="h-3 w-3" />,
  Coffee: <Coffee className="h-3 w-3" />,
  Printer: <Printer className="h-3 w-3" />,
  Joystick: <Joystick className="h-3 w-3" />,
  Refrigerator: <Refrigerator className="h-3 w-3" />,
  WashingMachine: <WashingMachine className="h-3 w-3" />,
  AirVent: <AirVent className="h-3 w-3" />,
};

export function HomeHero() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      navigate({ to: '/items', search: { query: q } });
    }
  }

  return (
    <section className="relative overflow-hidden pt-16 pb-10 md:pt-24 md:pb-14">
      {/* Background gradient */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-blue-600/5 to-transparent dark:from-blue-950/20" />

      {/* Floating orbs */}
      <div className="pointer-events-none absolute -top-20 -end-20 h-72 w-72 rounded-full bg-blue-500/10 blur-3xl dark:bg-blue-400/8 animate-[float_25s_ease-in-out_infinite]" />
      <div className="pointer-events-none absolute top-40 -start-16 h-56 w-56 rounded-full bg-purple-500/8 blur-3xl dark:bg-purple-400/6 animate-[float_20s_ease-in-out_infinite_8s]" />
      <div className="pointer-events-none absolute -bottom-8 end-1/3 h-48 w-48 rounded-full bg-emerald-500/8 blur-3xl dark:bg-emerald-400/6 animate-[float_22s_ease-in-out_infinite_4s]" />

      <div className="container relative mx-auto flex flex-col items-center px-4 text-center">
        {/* Brand + Headline */}
        <div className="mb-3 flex items-center justify-center gap-3">
          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-purple-600 shadow-lg">
            <div className="text-2xl font-bold text-white">PD</div>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-white/20 to-transparent opacity-60" />
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl bg-gradient-to-l from-blue-600 via-foreground to-foreground bg-clip-text text-transparent dark:from-blue-400">
            Pricedrop
          </h1>
        </div>

        <h2 className="mb-2 text-xl font-extrabold tracking-tight sm:text-2xl md:text-3xl">
          {m.home_headline()}
        </h2>

        <p className="mb-4 max-w-2xl text-sm text-muted-foreground sm:text-base">
          עם Pricedrop תוכלו לצפות בהיסטוריית המחירים של כל מוצרי KSP, לדעת מתי כדאי לקנות ולמצוא את
          המוצרים עם המחירים הטובים ביותר.
        </p>

        {/* Search form with focus glow */}
        <form onSubmit={handleSearch} className="relative w-full max-w-2xl group">
          <div className="pointer-events-none absolute -inset-1 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-blue-500/20 opacity-0 blur-lg transition-opacity duration-500 group-focus-within:opacity-100" />
          <div className="relative">
            <Search className="absolute start-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground transition-colors group-focus-within:text-blue-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              type="search"
              placeholder={m.common_searchProducts()}
              className="h-14 w-full rounded-2xl border border-border/60 bg-background/80 ps-14 pe-5 text-base shadow-lg shadow-black/5 backdrop-blur-sm transition-all duration-300 placeholder:text-muted-foreground/70 focus:border-blue-400 focus:shadow-xl focus:shadow-blue-500/10 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-background/60 dark:border-border/40 dark:focus:shadow-blue-400/15"
            />
          </div>
        </form>

        {/* Category badges */}
        <div className="mt-6 flex w-full max-w-2xl flex-nowrap gap-2 overflow-x-auto pb-2 snap-x sm:flex-wrap sm:justify-center no-scrollbar">
          {FEATURED_CATEGORIES.map((cat) => (
            <Link
              key={cat.tagId}
              to={itemsHref({ filters: [cat.tagId] })}
              className="shrink-0 snap-start"
            >
              <Badge
                variant="secondary"
                className="cursor-pointer gap-1.5 transition-colors hover:bg-accent whitespace-nowrap"
              >
                {ICON_MAP[cat.iconName]}
                {cat.label()}
              </Badge>
            </Link>
          ))}
        </div>

        {/* Trust bar */}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-500" />
            {m.home_trust_products()}
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-500" />
            {m.home_trust_tracking()}
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-500" />
            {m.home_trust_freeAlerts()}
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="h-4 w-4 text-emerald-500" />
            {m.home_trust_reviews()}
          </span>
        </div>
      </div>
    </section>
  );
}
